import { FC, useCallback, useEffect } from 'react'
import { cn } from '@bem-react/classname'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useNavigate } from 'react-router-dom'

import { Memo } from '@/hoc/Memo'
import { changeAuth } from '../AccountPage/store/accont.slice'
import { ForgotPasswordModel } from '@/core/services/Auth/models/AuthModel'
import { forgotPasswordThunk } from '@/core/services/Auth/store/auth.slice'
import useBackendError from '@/hooks/useBackendError'
import yup from '@/utils/yup-extended'
import { useAppDispatch } from '@/core/store/hooks'
import { setToast } from '@/core/services/Toast/store/toast.slice'
import { ToastType } from '@/core/services/Toast/types/ToastType'

import { Title } from '@/components/Title'
import { FormGroup } from '@/components/form/FormGroup'
import { Field } from '@/components/form/Field'
import { Button } from '@/components/Button'
import { Back } from '@/components/Back'

import './styles/ForgotPassword.scss'

const cnForgotPassword = cn('ForgotPassword')

export const ForgotPassword: FC = Memo(() => {
  const { t } = useTranslation('translation')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [setBackendError] = useBackendError()

  useEffect(() => {
    localStorage.removeItem('token')
    dispatch(changeAuth(false))
  }, [])

  const ForgotPasswordSchema = yup.object().shape({
    email: yup
      .string()
      .trim()
      .email(t('Email is not correct'))
      .required(t('Email is a required field')),
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordModel>({
    mode: 'onChange',
    resolver: yupResolver(ForgotPasswordSchema),
  })

  const forgotPassword = useCallback(async (email: ForgotPasswordModel) => {
    try {
      const res = await dispatch(forgotPasswordThunk(email)).unwrap()

      if (res === 200) {
        dispatch(
          setToast({
            type: ToastType.success,
            message: t(
              'Password reset information has been successfully emailed to',
            ),
          }),
        )

        navigate('/')
      }
    } catch (errors) {
      setBackendError(errors)
    }
  }, [])

  return (
    <>
      <Helmet>
        <title>{t('Forgot Password')} | Registration</title>
      </Helmet>
      <div className={cnForgotPassword()}>
        <Back />
        <div className={cnForgotPassword('wrap')}>
          <form
            className={cnForgotPassword('form')}
            onSubmit={handleSubmit(forgotPassword)}
          >
            <Title className={cnForgotPassword('title')}>
              {t('Forgot Password')}?
            </Title>
            <div className={cnForgotPassword('text')}>
              {t('Enter your Email to receive the password')}
            </div>
            <FormGroup>
              <Field
                {...register('email')}
                placeholder={t('Enter email')}
                label={t('Email')}
                required
                errors={errors.email && errors.email.message}
              />
            </FormGroup>
            <Button type='submit' isLoading={isSubmitting} large>
              {t('Submit')}
            </Button>
          </form>
          <div className={cnForgotPassword('img')}>
            <img src='/img/dtm.jpeg' alt='Dinamica Generale' />
          </div>
        </div>
      </div>
    </>
  )
})
